import { useMutation, useQueryClient } from "@tanstack/react-query";

import { stateKey, useRoomState } from "../documents/useDocuments";
import type { CoService, ContentOptimization } from "../schema";
import type { CoDeliverable, CoOrderStatus } from "./status";
import { applyBulkRule, lockedCells, setColumn as setColumnCells, toggleCell, type BulkRule, type CoSelection } from "./selection";
import type { CoAssortmentRow } from "../schema";

export interface CoOrderView {
    id: string;
    at: string;
    by: string;
    byName?: string;
    cells: Record<string, string[]>;
    rates: Record<string, number>;
    asins: number;
    services: number;
    total: number;
    termsVersion: string;
    termsText: string;
    status?: CoOrderStatus;
    statusLog?: { status: CoOrderStatus; at: string; by: string }[];
    deliverables?: CoDeliverable[];
}

// The shared cart, read from and written to the room state sidecar. Every
// write is one POST of the rows that changed (a hand tick sends one ASIN, the
// bulk rule sends every row it touched plus the rows it clears), applied
// optimistically so the matrix never waits on the round trip.
export function useCoSelection(slug: string) {
    const client = useQueryClient();
    const state = useRoomState(slug);
    const data = state.data as { coSelection?: CoSelection; coOrders?: CoOrderView[]; coRule?: BulkRule } | undefined;
    const selection = data?.coSelection ?? {};
    // The filter the cart was last built from; null until one is saved.
    const rule = data?.coRule ?? null;
    const orders = data?.coOrders ?? [];
    const locked = lockedCells(orders);

    const mutation = useMutation({
        mutationFn: async ({ patch, coRule }: { patch: Record<string, CoService[] | null>; coRule?: BulkRule }) => {
            const res = await fetch(`/api/rooms/${slug}/state`, {
                method: "POST",
                credentials: "include",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ coSelection: patch, ...(coRule && { coRule }) }),
            });
            if (!res.ok) throw new Error(`selection save failed: ${res.status}`);
            return (await res.json()) as { coSelection?: CoSelection };
        },
        onMutate: async ({ patch, coRule }) => {
            await client.cancelQueries({ queryKey: stateKey(slug) });
            const previous = client.getQueryData(stateKey(slug));
            client.setQueryData(stateKey(slug), (old: { coSelection?: CoSelection } | undefined) => {
                const next: CoSelection = { ...(old?.coSelection ?? {}) };
                for (const [asin, services] of Object.entries(patch)) {
                    if (!services || services.length === 0) delete next[asin];
                    else next[asin] = services;
                }
                return { ...(old ?? {}), coSelection: next, ...(coRule && { coRule }) };
            });
            return { previous };
        },
        onError: (_err, _patch, ctx) => {
            if (ctx?.previous) client.setQueryData(stateKey(slug), ctx.previous);
        },
        onSuccess: (data) => {
            client.setQueryData(stateKey(slug), (old: object | undefined) => ({ ...(old ?? {}), ...data }));
        },
    });

    // Save a filter and rebuild the whole cart from it (Eric, 2026-09-24: the
    // grid follows every filter change; hand ticks last until the next one).
    // One POST carries the rule and the patch; rows the rule no longer ticks
    // are cleared.
    const apply = (next: BulkRule, rows: readonly CoAssortmentRow[], rates?: ContentOptimization["rates"]) => {
        const built = applyBulkRule(rows, next, locked, rates);
        const patch: Record<string, CoService[] | null> = {};
        for (const asin of Object.keys(selection)) patch[asin] = null;
        for (const [asin, services] of Object.entries(built)) patch[asin] = services;
        mutation.mutate({ patch, coRule: next });
    };

    // A hand tick reads the LATEST cart from the query cache, not from the
    // render that drew the cell: the data grid keeps cell render closures
    // across quick successive clicks, and a stale cart made the second tick
    // overwrite the first (caught in the browser, 2026-09-23).
    const toggle = (row: CoAssortmentRow, service: CoService) => {
        const latest = (client.getQueryData(stateKey(slug)) as { coSelection?: CoSelection } | undefined)?.coSelection ?? {};
        const next = toggleCell(latest, row, service, locked);
        mutation.mutate({ patch: { [row.asin]: next[row.asin] ?? [] } });
    };

    // A header tick: select or clear the whole column, from the latest cart.
    const setColumn = (rows: readonly CoAssortmentRow[], service: CoService, on: boolean) => {
        const latest = (client.getQueryData(stateKey(slug)) as { coSelection?: CoSelection } | undefined)?.coSelection ?? {};
        const patch = setColumnCells(latest, rows, service, on, locked);
        if (Object.keys(patch).length) mutation.mutate({ patch });
    };

    return {
        selection,
        rule,
        orders,
        locked,
        loading: state.isLoading,
        saving: mutation.isPending,
        error: mutation.error ?? state.error,
        toggle,
        setColumn,
        apply,
    };
}
